import type { MenuProps } from 'antd';
import { Menu, Badge } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchAllCategories from '../../Hooks/useFetchAllCategories';
import CategoriesSkeleton from './CategoriesSkeleton';
import './list.css';

type MenuItem = Required<MenuProps>['items'][number];

const CategoriesList: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { categories, subcategories, loading } = useFetchAllCategories();

    const handleSubCategoryClick = (categoryId: string, subCategoryId: string) => {
        const category = categories.find(cat => cat.id === categoryId);
        const subcategory = subcategories.find(sub => sub.id === subCategoryId);
        
        if (category && subcategory) {
            // Use encodeURIComponent to handle special characters in names
            const categoryName = encodeURIComponent(category.name);
            const subcategoryName = encodeURIComponent(subcategory.name);
            navigate(`/search/${categoryName}/${subcategoryName}`);
        }
    };

    const buildMenuItems = (): MenuItem[] => {
        // Show categories with the most ads first
        const sortedCategories = [...categories].sort(
            (a, b) => (b.adCount || 0) - (a.adCount || 0)
        );

        return sortedCategories.map((category) => ({
            key: category.id,
            label: (
                <div className="flex items-center justify-between  gap-3 py-1 px-4 hover:bg-gray-50 rounded-md transition-colors my-1 w-full">
                    <div className="flex items-center gap-3 ">
                        <img
                            src={category.image}
                            alt={t(`categories.${category.id}.name`)}
                            className="w-8 h-8 object-contain"
                        />
                        <div className="">
                            <span className="font-medium text-gray-700">{category.name}</span>
                            <div className='flex items-center gap-1 -mt-2'>
                                <span className='font-bold text-xs text-gray-500'>{category.adCount || 0} </span>
                                <span className=" text-xs text-gray-500">articles</span>
                            </div>
                        </div>
                    </div>
                </div>
            ),
            children: subcategories
                .filter((subcategory) => subcategory.category_id === category.id)
                // Show subcategories with the most ads first
                .sort((a, b) => (b.adCount || 0) - (a.adCount || 0))
                .map((subcategory) => ({
                    key: `${category.id}-${subcategory.id}`,
                    label: (
                        <div
                            className="flex items-center justify-between gap-3 py-1 px-4 hover:bg-gray-50 rounded-md transition-colors cursor-pointer my-1 w-full"
                            onClick={() => handleSubCategoryClick(category.id, subcategory.id)}
                        >
                            <div className="flex items-center gap-3">
                                <img
                                    src={subcategory.image}
                                    alt={t(`categories.${category.id}.items.${subcategory.id}`)}
                                    className="w-8 h-8 object-contain"
                                />
                                <span className="text-gray-600">{subcategory.name}</span>
                            </div>
                            <div className="flex items-center">
                                <Badge
                                    count={subcategory.adCount}
                                    showZero
                                    style={{
                                        backgroundColor: subcategory.adCount ? '#1677ff' : '#d9d9d9',
                                        fontSize: '11px'
                                    }}
                                />
                                <span className="ml-1 text-xs text-gray-500">articles</span>
                            </div>
                        </div>
                    ),
                })),
        }));
    };

    const items: MenuItem[] = buildMenuItems();

    return (
        <div className="w-full">
            {loading ? (
                <CategoriesSkeleton />
            ) : (
                <Menu
                    mode="vertical"
                    theme="light"
                    items={items}
                    className="border-none space-y-4"
                    style={{
                        width: '100%',
                        '--item-gap': '16px'
                    } as React.CSSProperties}
                />
            )}
        </div>
    );
};

export default CategoriesList;